import type { HelpPage } from './registry'

/**
 * Wiki search (z8nrz7dyd3): every word must appear, "a quoted phrase" must
 * appear exactly, case-insensitive, over each page's title and body text.
 *
 * Pure and synchronous: the wiki is markdown bundled into the app, so the whole
 * index is in memory and there is nothing to fetch. Pages are filtered by
 * permission BEFORE anything is matched, so a page a reader may not open can
 * never reach a result or a snippet (AC8).
 */

/** Snippets shown per page at most (AC4). */
export const SNIPPETS_PER_PAGE = 3

/** Characters of context either side of a match. */
const CONTEXT = 70

export interface ParsedQuery {
  /** Single words, lower-cased. Every one must appear. */
  terms: string[]
  /** Quoted phrases, lower-cased with whitespace collapsed. Each must appear as written. */
  phrases: string[]
}

/**
 * Typographic double quotes — what macOS smart quotes (and text pasted from a
 * document) put in the box — are quotes too, so `“storage unit”` is a phrase,
 * not the words `“storage` and `unit”`.
 */
const SMART_QUOTES = /[\u201C\u201D\u201E\u201F\u00AB\u00BB\u2033\uFF02]/g

/** `stem cell "storage unit"` → terms [stem, cell], phrases [storage unit]. */
export function parseQuery(raw: string): ParsedQuery {
  const phrases: string[] = []
  const rest = raw.replace(SMART_QUOTES, '"').replace(/"([^"]*)"/g, (_, phrase: string) => {
    const p = collapse(phrase).toLowerCase()
    if (p) phrases.push(p)
    return ' '
  })
  // An unpaired quote is just a character to ignore, not the start of a phrase.
  const terms = rest.replace(/"/g, ' ').toLowerCase().split(/\s+/).filter(Boolean)
  return { terms: dedupe(terms), phrases: dedupe(phrases) }
}

/** Whether there is anything to search for. */
export function isEmptyQuery(q: ParsedQuery): boolean {
  return q.terms.length === 0 && q.phrases.length === 0
}

/**
 * A page's markdown as the text a reader sees: markup, link targets, image
 * paths and diagram source removed, whitespace collapsed. Code samples stay —
 * they are text on the page. So do structured blocks (```cards, ```steps),
 * minus the `icon:` / `tone:` / `link:` / `image:` / `id:` / `short:` / `needs:` / `video:` lines
 * that only style or wire them
 * and the `badge:` / `meta:` / `check:` key names (their values are text on
 * the page).
 */
export function plainText(markdown: string): string {
  return collapse(
    markdown
      .replace(/```mermaid[\s\S]*?```/g, ' ')
      .replace(/^\s*(icon|tone|link|image|id|short|needs|video):\s.*$/gm, ' ')
      .replace(/^\s*(badge|meta|check):\s*/gm, '')
      .replace(/^\s*(```|~~~).*$/gm, ' ')
      .replace(/<!--[\s\S]*?-->/g, ' ')
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/<[^>]+>/g, ' ')
      .replace(/^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/gm, ' ')
      .replace(/^\s{0,3}(#{1,6}|>+|[-*+]|\d+[.)])\s+/gm, '')
      .replace(/[|`*~]/g, ' ')
      // Emphasis underscores go; the ones inside a word (`source_id`) are text.
      .replace(/(?<![A-Za-z0-9])_+|_+(?![A-Za-z0-9])/g, ' '),
  )
}

/** A run of snippet text, `hit` when it is the matched word or phrase. */
export interface SnippetPart {
  text: string
  hit: boolean
}

export interface SearchResult {
  page: HelpPage
  /** The title, split for highlighting — a title (metadata) hit ranks first (AC3). */
  title: SnippetPart[]
  titleHit: boolean
  /** Up to SNIPPETS_PER_PAGE passages the match came from, in page order (AC4). */
  snippets: SnippetPart[][]
  /** Matches in the body text, including those past the snippet limit. */
  bodyMatches: number
}

export interface SearchOptions {
  /** Whether the reader may open this page; checked before matching (AC8). */
  canAccess: (page: HelpPage) => boolean
  /** Narrow to one section (`user-manual`, `dev-docs`, `releases`, '' for the home page); all when undefined. */
  section?: string
}

export interface SearchOutcome {
  results: SearchResult[]
  /** Matches per section before the section filter, so every filter shows its count (AC5). */
  bySection: Record<string, number>
}

const bodyCache = new Map<string, string>()

function bodyOf(page: HelpPage): string {
  const key = `${page.urlPath}\u0000${page.content}`
  let body = bodyCache.get(key)
  if (body === undefined) {
    body = plainText(page.content)
    bodyCache.set(key, body)
  }
  return body
}

export function searchPages(pages: readonly HelpPage[], raw: string, options: SearchOptions): SearchOutcome {
  const query = parseQuery(raw)
  if (isEmptyQuery(query)) return { results: [], bySection: {} }
  const needles = [...query.phrases, ...query.terms]

  const matched: SearchResult[] = []
  for (const page of pages) {
    if (!options.canAccess(page)) continue
    const body = bodyOf(page)
    const haystack = `${page.title.toLowerCase()}\n${body.toLowerCase()}`
    if (!needles.every(n => haystack.includes(n))) continue

    const bodyRanges = findRanges(body, needles)
    const titleRanges = findRanges(page.title, needles)
    matched.push({
      page,
      title: highlight(page.title, titleRanges),
      titleHit: titleRanges.length > 0,
      snippets: snippetsFor(body, bodyRanges),
      bodyMatches: bodyRanges.length,
    })
  }

  const bySection: Record<string, number> = {}
  for (const r of matched) bySection[r.page.section] = (bySection[r.page.section] ?? 0) + 1

  const results = matched
    .filter(r => options.section === undefined || r.page.section === options.section)
    .sort(
      (a, b) =>
        Number(b.titleHit) - Number(a.titleHit) ||
        b.bodyMatches - a.bodyMatches ||
        a.page.title.localeCompare(b.page.title),
    )
  return { results, bySection }
}

// ─── Matching and snippets ──────────────────────────────────────────────────

type Range = [start: number, end: number]

/** Every occurrence of every needle in `text`, case-insensitive, overlapping ones merged. */
function findRanges(text: string, needles: readonly string[]): Range[] {
  const lower = text.toLowerCase()
  const ranges: Range[] = []
  for (const needle of needles) {
    for (let at = lower.indexOf(needle); at !== -1; at = lower.indexOf(needle, at + needle.length)) {
      ranges.push([at, at + needle.length])
    }
  }
  return merge(ranges)
}

function merge(ranges: Range[]): Range[] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0] || b[1] - a[1])
  const out: Range[] = []
  for (const r of sorted) {
    const last = out[out.length - 1]
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1])
    else out.push([r[0], r[1]])
  }
  return out
}

/** Passages around the matches: windows that overlap are joined, the first few kept. */
function snippetsFor(body: string, hits: Range[]): SnippetPart[][] {
  const windows = merge(hits.map(([s, e]) => [Math.max(0, s - CONTEXT), Math.min(body.length, e + CONTEXT)]))
  return windows.slice(0, SNIPPETS_PER_PAGE).map(([s, e]) => {
    const start = wordStart(body, s)
    const end = wordEnd(body, e)
    const inside = hits
      .filter(([hs, he]) => hs < end && he > start)
      .map(([hs, he]): Range => [Math.max(hs, start) - start, Math.min(he, end) - start])
    const parts = highlight(body.slice(start, end), inside)
    if (start > 0) parts.unshift({ text: '…', hit: false })
    if (end < body.length) parts.push({ text: '…', hit: false })
    return parts
  })
}

/** Move a cut back to the start of the word it lands in, so a snippet never opens mid-word. */
function wordStart(text: string, at: number): number {
  if (at <= 0) return 0
  const space = text.lastIndexOf(' ', at)
  return space === -1 ? 0 : space + 1
}

function wordEnd(text: string, at: number): number {
  if (at >= text.length) return text.length
  const space = text.indexOf(' ', at)
  return space === -1 ? text.length : space
}

function highlight(text: string, ranges: readonly Range[]): SnippetPart[] {
  const parts: SnippetPart[] = []
  let cursor = 0
  for (const [s, e] of ranges) {
    if (s > cursor) parts.push({ text: text.slice(cursor, s), hit: false })
    parts.push({ text: text.slice(s, e), hit: true })
    cursor = e
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), hit: false })
  return parts
}

function collapse(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

function dedupe(values: string[]): string[] {
  return [...new Set(values)]
}
